import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { List, X, ArrowUpRight } from "@phosphor-icons/react";
import { CLINIC, IMAGES } from "../data/site";
import { EnquiryDialog } from "./EnquiryDialog";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/treatments", label: "Treatments" },
  { to: "/pricing", label: "Pricing" },
  { to: "/gallery", label: "Before & After" },
  { to: "/team", label: "Team" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const isHome = pathname === "/";
  const solid = scrolled || !isHome;
  const linkTone = solid
    ? "text-foreground/80 hover:text-primary"
    : "text-white/85 hover:text-white";

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
          solid ? "border-b border-border/70 bg-background/90 backdrop-blur-md" : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
          <Link to="/" className="group flex shrink-0 items-center gap-3" data-testid="nav-logo">
            <img src={IMAGES.logo} alt="DK Aesthetics logo" className="h-11 w-11 rounded-full border border-border bg-white object-cover transition-transform duration-500 group-hover:scale-105" />
            <span className="whitespace-nowrap leading-none">
              <span className={`block font-display text-xl font-medium tracking-[0.08em] transition-colors duration-500 ${solid ? "" : "text-white"}`}>
                DK <span className={solid ? "text-primary" : "text-white/80"}>Aesthetics</span>
              </span>
              <span className={`mt-1 block text-[10px] uppercase tracking-[0.3em] transition-colors duration-500 ${solid ? "text-muted-foreground" : "text-white/60"}`}>
                {CLINIC.tagline}
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-5 xl:gap-7 lg:flex" data-testid="nav-desktop">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative whitespace-nowrap text-[13px] uppercase tracking-[0.2em] transition-colors duration-300 ${linkTone} ${
                    isActive ? (solid ? "!text-primary" : "!text-white") : ""
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href="https://ivtherapybydkaesthetics.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-link-iv-therapy"
              className={`flex items-center gap-1 whitespace-nowrap text-[13px] uppercase tracking-[0.2em] transition-colors duration-300 ${linkTone}`}
            >
              IV Therapy <ArrowUpRight size={13} />
            </a>
          </nav>

          <div className="hidden lg:block">
            <EnquiryDialog
              defaultType="consultation"
              trigger={
                <button
                  data-testid="nav-book-consultation-button"
                  className="bg-primary px-6 py-3 text-[12px] font-medium uppercase tracking-[0.25em] text-primary-foreground transition-colors duration-300 hover:bg-primary/90"
                >
                  Book Consultation
                </button>
              }
            />
          </div>

          <button
            className={`p-2 lg:hidden ${solid ? "" : "text-white"}`}
            onClick={() => setOpen(true)}
            data-testid="nav-mobile-menu-button"
            aria-label="Open menu"
          >
            <List size={26} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] flex flex-col bg-background"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            data-testid="nav-mobile-overlay"
          >
            <div className="flex h-20 items-center justify-between px-5">
              <span className="font-display text-xl">DK <span className="text-primary">Aesthetics</span></span>
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="nav-mobile-close-button" className="p-2">
                <X size={26} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                    className="block border-b border-border/60 py-4 font-display text-4xl font-light tracking-tight transition-colors duration-300 hover:text-primary"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-8 flex flex-col gap-3">
                <EnquiryDialog
                  defaultType="consultation"
                  trigger={
                    <button data-testid="nav-mobile-book-button" className="bg-primary px-6 py-4 text-xs font-medium uppercase tracking-[0.25em] text-primary-foreground">
                      Book free consultation
                    </button>
                  }
                />
                <a href={CLINIC.phoneHref} data-testid="nav-mobile-call-link" className="border border-border px-6 py-4 text-center text-xs uppercase tracking-[0.25em]">
                  Call {CLINIC.phone}
                </a>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
